import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { Container } from 'src/components/layouts/Container/Container';
import TabBar, { TabProps } from 'src/components/layouts/TabBar/TabBar';
import {
  CustomerForecastSummary,
  ForecastState,
  STATE_LABEL_KEY,
  useAddForecastMonth,
  useForecast,
  useOwnForecasts,
} from 'src/services/forecasts';
import Select, { Option } from 'src/components/ui/Select/Select';
import Modal from 'src/components/layouts/Modal/Modal';
import {
  CustomerForecastPromotion,
  PROMOTION_STATE_LABEL_KEY,
  PROMOTION_TYPE_LABEL_KEY,
  PromotionState,
  PromotionType,
  useDeletePromotion,
  useOwnPromotions,
} from 'src/services/forecastPromotions';
import { toast } from 'react-toastify';
import RequestPromotionDeleteModal from 'src/components/modals/RequestPromotionDeleteModal/RequestPromotionDeleteModal';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { currentLocale, localeNumber } from 'src/localization/locale';
import PromotionPeriodModal from './PromotionPeriodModal';
import ForecastWizard from './ForecastWizard';
import ForecastSubmittedView from './ForecastSubmittedView';
import { addableMonths, isInPlanningWindow } from './planningWindow';

const STATE_BADGE_TYPE: Record<ForecastState, BadgeType> = {
  draft: BadgeType.Neutral,
  submitted: BadgeType.Neutral2,
  approved: BadgeType.Successful,
  rejected: BadgeType.Error,
};

const PROMOTION_TYPE_BADGE_TYPE: Partial<Record<PromotionType, BadgeType>> = {
  black_week: BadgeType.Neutral2,
  sale: BadgeType.Neutral3,
  launch: BadgeType.Neutral4,
  campaign: BadgeType.Neutral5,
  season: BadgeType.Neutral6,
  other: BadgeType.Neutral,
};

const PROMO_STATE_BADGE_TYPE: Record<PromotionState, BadgeType> = {
  draft: BadgeType.Neutral,
  submitted: BadgeType.Neutral2,
  approved: BadgeType.Successful,
  rejected: BadgeType.Error,
  active: BadgeType.Successful,
  completed: BadgeType.Neutral,
};

function fmtYearMonth(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), {
    year: 'numeric',
    month: 'long',
  });
}

function fmtDate(d?: string | null): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtPercent(n: number): string {
  return localeNumber(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function fmtNumber(n?: number | null): string {
  if (n === null || n === undefined) return '—';
  return localeNumber(n);
}

function StateBadge({ state }: { state: ForecastState }) {
  const { t } = useTranslation();
  return <Badge className="max-w-min" label={t(STATE_LABEL_KEY[state])} expanded type={STATE_BADGE_TYPE[state]} />;
}

function isOverdue(row: CustomerForecastSummary): boolean {
  return row.is_overdue;
}

// ── Overdue banner ─────────────────────────────────────────────────────────

function OverdueBanner({ rows, onOpen }: { rows: CustomerForecastSummary[]; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const overdue = rows.filter(isOverdue);
  if (overdue.length === 0) return null;
  const months = overdue
    .slice()
    .sort((a, b) => (a.year === b.year ? b.month - a.month : b.year - a.year))
    .map(r => fmtYearMonth(r.year, r.month))
    .join(', ');
  const first = overdue[0];
  return (
    <section className="status-error-bg border border-subtle rounded-base p-16 flex items-center justify-between gap-16">
      <div>
        <div className="typo-text-14--bold status-error-full">
          {t('forecasts.overdue.banner', { count: overdue.length })}
        </div>
        <div className="typo-text-12 status-error-full">{months}</div>
      </div>
      <Button
        tone={ButtonToneProps.DESTRUCTIVE}
        title={t('forecasts.overdue.enter')}
        onClick={() => onOpen(first.id)}
      />
    </section>
  );
}

// ── Forecast Accuracy (last completed month) ──────────────────────────────
//
// Shows how well the customer's forecast matched the actuals for the most
// recent month that has approved + finalized actuals (total_actual > 0).
// Renders nothing until the actuals-poll has populated at least one such
// month — no empty stub.

function AccuracyTile({
  title,
  subtitle,
  value,
  valueClass,
  note,
}: {
  title: string;
  subtitle: string;
  value: string;
  valueClass?: string;
  note?: string;
}) {
  return (
    <div className="flex-1 min-w-[160px] min-h-[136px] flex flex-col justify-between gap-10 p-20 border-subtle-right">
      <div className="flex flex-col gap-y-4">
        <h2 className="typo-text-16--bold font-semibold text-full">{title}</h2>
        <span className="typo-text-12 text-faded">{subtitle}</span>
      </div>
      <div className="flex flex-col gap-2">
        <p className={`typo-title-24 lg:typo-title-36 !font-bold ${valueClass ?? 'text-full'}`}>{value}</p>
        {note && <span className="typo-text-12 text-faded">{note}</span>}
      </div>
    </div>
  );
}

function ForecastAccuracyCard({ rows }: { rows: CustomerForecastSummary[] }) {
  const { t } = useTranslation();

  const selectable = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth() + 1;
    return rows
      .filter(r => r.year < curY || (r.year === curY && r.month <= curM))
      .slice()
      .sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year));
  }, [rows]);

  const defaultIdx = useMemo(() => {
    for (let i = selectable.length - 1; i >= 0; i -= 1) {
      if ((selectable[i].total_actual ?? 0) > 0) return i;
    }
    return selectable.length - 1;
  }, [selectable]);

  const [selIdx, setSelIdx] = useState<number | null>(null);

  if (selectable.length === 0) return null;

  const idx = Math.min(Math.max(selIdx ?? defaultIdx, 0), selectable.length - 1);
  const cur = selectable[idx];
  const hasActuals = cur.actuals_filled_days > 0;
  // Boosted (operativ) is the effective planned figure and the deviation base;
  // the original pre-promotion total is shown as a secondary note.
  const original = cur.total_pakete;
  const soll = cur.total_pakete_boosted ?? original;
  const hasBoost = soll > original;
  const ist = cur.total_actual ?? 0;
  const moeglich = cur.total_possible ?? 0;
  const deviationPct = hasActuals && soll ? Math.round(((ist - soll) / soll) * 1000) / 10 : null;
  let deviationColor = 'text-full';
  if (deviationPct !== null) {
    deviationColor = Math.abs(deviationPct) < 5 ? 'status-success-full' : 'status-error-full';
  }
  const canPrev = idx > 0;
  const canNext = idx < selectable.length - 1;
  const arrowClass = (enabled: boolean) =>
    `inline-flex p-4 ${enabled ? 'text-faded hover:text-full cursor-pointer' : 'opacity-30 cursor-not-allowed'}`;

  return (
    <section className="surface-elevated border border-subtle rounded-base card-shadow overflow-hidden">
      <div className="flex items-center justify-between px-20 py-12 border-subtle-bottom">
        <h2 className="typo-text-14--bold text-full">{t('forecasts.accuracy.title')}</h2>
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label={t('forecasts.accuracy.prev')}
            disabled={!canPrev}
            onClick={() => setSelIdx(idx - 1)}
            className={arrowClass(canPrev)}>
            <Icon name="chevronLeft" width={18} height={18} />
          </button>
          <span className="typo-text-14--medium text-full text-center min-w-[120px]">
            {fmtYearMonth(cur.year, cur.month)}
          </span>
          <button
            type="button"
            aria-label={t('forecasts.accuracy.next')}
            disabled={!canNext}
            onClick={() => setSelIdx(idx + 1)}
            className={arrowClass(canNext)}>
            <Icon name="chevronRight" width={18} height={18} />
          </button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row">
        <AccuracyTile
          title={t('forecasts.col.forecast')}
          subtitle={t('forecasts.accuracy.sub_planned')}
          value={fmtNumber(soll)}
          note={hasBoost ? t('forecasts.plannedOriginal', { value: fmtNumber(original) }) : undefined}
        />
        <AccuracyTile
          title={t('forecasts.col.actual')}
          subtitle={t('forecasts.accuracy.sub_shipped')}
          value={hasActuals ? fmtNumber(ist) : '—'}
        />
        <AccuracyTile
          title={t('forecasts.col.possible')}
          subtitle={t('forecasts.accuracy.sub_possible')}
          value={hasActuals ? fmtNumber(moeglich) : '—'}
        />
        <AccuracyTile
          title={t('forecasts.col.deviation')}
          subtitle={t('forecasts.accuracy.sub_deviation')}
          value={deviationPct === null ? '—' : `${deviationPct > 0 ? '+' : ''}${fmtPercent(deviationPct)}%`}
          valueClass={deviationColor}
        />
      </div>
    </section>
  );
}

// ── Promotion periods ──────────────────────────────────────────────────────

function promoValue(promo: CustomerForecastPromotion): string {
  if (promo.multiplier) return `${promo.multiplier.toFixed(2)}x`;
  return `+${promo.additional_orders ?? 0}`;
}

type PromoFormModal = {
  kind: 'form';
  mode: 'create' | 'edit' | 'request-edit';
  item?: CustomerForecastPromotion;
};
type PromoDeleteModal = { kind: 'delete-request'; item: CustomerForecastPromotion };
type PromoConfirmDeleteModal = { kind: 'delete-confirm'; item: CustomerForecastPromotion };
type PromoModalState = PromoFormModal | PromoDeleteModal | PromoConfirmDeleteModal | null;

const DIRECT_ACTION_STATES: PromotionState[] = ['draft', 'rejected', 'submitted'];
const REQUEST_ACTION_STATES: PromotionState[] = ['approved', 'active'];

function PromotionIconButton({
  icon,
  variant,
  enabled,
  ariaLabel,
  hoverText,
  onClick,
}: {
  icon: 'edit' | 'delete';
  variant: 'edit' | 'delete';
  enabled: boolean;
  ariaLabel: string;
  hoverText: string;
  onClick: () => void;
}) {
  const iconClass = variant === 'delete' ? 'status-error-full' : 'text-faded';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={enabled ? onClick : undefined}
      className={`inline-flex items-center justify-center p-6 ${
        enabled ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'
      }`}>
      <Icon name={icon} width={16} height={16} className={iconClass} hoverText={hoverText} />
    </button>
  );
}

function PromotionTableRow({
  promo,
  onEdit,
  onRequestEdit,
  onRequestDelete,
  onDeleteDirect,
}: {
  promo: CustomerForecastPromotion;
  onEdit: (p: CustomerForecastPromotion) => void;
  onRequestEdit: (p: CustomerForecastPromotion) => void;
  onRequestDelete: (p: CustomerForecastPromotion) => void;
  onDeleteDirect: (p: CustomerForecastPromotion) => void;
}) {
  const { t } = useTranslation();

  const isDirect = DIRECT_ACTION_STATES.includes(promo.state);
  const isRequestable = REQUEST_ACTION_STATES.includes(promo.state);
  const enabled = !promo.pending_change && (isDirect || isRequestable);

  let disabledReason = t('forecastPromotions.disabledGeneric');
  if (promo.pending_change === 'delete') disabledReason = t('forecastPromotions.disabledPendingDelete');
  else if (promo.pending_change === 'edit') disabledReason = t('forecastPromotions.disabledPendingEdit');
  else if (promo.state === 'submitted') disabledReason = t('forecastPromotions.disabledSubmitted');
  else if (promo.state === 'completed') disabledReason = t('forecastPromotions.disabledCompleted');

  const handleEdit = () => {
    if (isDirect) onEdit(promo);
    else if (isRequestable) onRequestEdit(promo);
  };
  const handleDelete = () => {
    if (isDirect) onDeleteDirect(promo);
    else if (isRequestable) onRequestDelete(promo);
  };

  const actions = (
    <div className="flex items-center justify-end gap-8">
      <PromotionIconButton
        icon="edit"
        variant="edit"
        enabled={enabled}
        ariaLabel={t('forecastPromotions.edit')}
        hoverText={enabled ? t('forecastPromotions.edit') : disabledReason}
        onClick={handleEdit}
      />
      <PromotionIconButton
        icon="delete"
        variant="delete"
        enabled={enabled}
        ariaLabel={t('forecastPromotions.delete')}
        hoverText={enabled ? t('forecastPromotions.delete') : disabledReason}
        onClick={handleDelete}
      />
    </div>
  );

  return (
    <tr className="border-b border-subtle">
      <td className="px-20 py-16 typo-text-14--medium text-full">{promo.name}</td>
      <td className="px-20 py-16">
        <Badge
          className="max-w-min"
          label={t(PROMOTION_TYPE_LABEL_KEY[promo.type])}
          expanded
          type={PROMOTION_TYPE_BADGE_TYPE[promo.type] ?? BadgeType.Neutral}
        />
      </td>
      <td className="px-20 py-16 typo-text-14 text-full">{fmtDate(promo.date_from)}</td>
      <td className="px-20 py-16 typo-text-14 text-full">{fmtDate(promo.date_to)}</td>
      <td className="px-20 py-16 typo-text-14 text-full">{promoValue(promo)}</td>
      <td className="px-20 py-16">
        <Badge
          className="max-w-min"
          label={t(PROMOTION_STATE_LABEL_KEY[promo.state])}
          expanded
          type={PROMO_STATE_BADGE_TYPE[promo.state]}
        />
      </td>
      <td className="px-20 py-16 text-right">{actions}</td>
    </tr>
  );
}

function PromotionsTable({
  rows,
  onEdit,
  onRequestEdit,
  onRequestDelete,
  onDeleteDirect,
}: {
  rows: CustomerForecastPromotion[];
  onEdit: (p: CustomerForecastPromotion) => void;
  onRequestEdit: (p: CustomerForecastPromotion) => void;
  onRequestDelete: (p: CustomerForecastPromotion) => void;
  onDeleteDirect: (p: CustomerForecastPromotion) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '13%' }} />
        </colgroup>
        <thead className="surface-base">
          <tr className="border-b border-subtle">
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.title')}
            </th>
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.type')}
            </th>
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.from')}
            </th>
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.to')}
            </th>
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.multiplier')}
            </th>
            <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.state')}
            </th>
            <th className="px-20 py-12 text-right typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastPromotions.col.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <PromotionTableRow
              key={p.id}
              promo={p}
              onEdit={onEdit}
              onRequestEdit={onRequestEdit}
              onRequestDelete={onRequestDelete}
              onDeleteDirect={onDeleteDirect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromotionPeriodsSection() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useOwnPromotions();
  const del = useDeletePromotion();
  const [modal, setModal] = useState<PromoModalState>(null);
  useEffect(() => {
    if (error) toast.error(t('forecastPromotions.loadFailed'));
  }, [error, t]);

  const confirmDelete = async (confirmed: boolean) => {
    const item = modal?.kind === 'delete-confirm' ? modal.item : null;
    setModal(null);
    if (!confirmed || !item) return;
    try {
      await del.mutateAsync(item.id);
    } catch {
      toast.error(t('forecastPromotions.deleteFailed'));
    }
  };

  return (
    <section className="surface-elevated border border-subtle rounded-base card-shadow overflow-hidden">
      <div className="flex items-center justify-between p-24 border-subtle-bottom">
        <h2 className="typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecastPromotions.sectionTitle')}
        </h2>
        <Button
          tone={ButtonToneProps.PRIMARY_BLUE}
          icon="plus"
          title={t('forecastPromotions.newPromoButton')}
          onClick={() => setModal({ kind: 'form', mode: 'create' })}
        />
      </div>
      {isLoading && <div className="p-24 typo-text-14 text-faded">{t('forecasts.loading')}</div>}
      {!isLoading && !error && (!data || data.length === 0) && (
        <div className="text-center py-40 typo-text-14 text-faded">{t('forecastPromotions.empty')}</div>
      )}
      {!isLoading && !error && data && data.length > 0 && (
        <PromotionsTable
          rows={data}
          onEdit={item => setModal({ kind: 'form', mode: 'edit', item })}
          onRequestEdit={item => setModal({ kind: 'form', mode: 'request-edit', item })}
          onRequestDelete={item => setModal({ kind: 'delete-request', item })}
          onDeleteDirect={item => setModal({ kind: 'delete-confirm', item })}
        />
      )}
      {modal?.kind === 'form' && (
        <PromotionPeriodModal mode={modal.mode} initial={modal.item} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'delete-request' && (
        <RequestPromotionDeleteModal promotion={modal.item} isOpen onClose={() => setModal(null)} />
      )}
      <ConfirmationModal
        isOpen={modal?.kind === 'delete-confirm'}
        title={t('forecastPromotions.confirmDeleteTitle')}
        description={t('forecastPromotions.confirmDelete')}
        onClose={confirmDelete}
      />
    </section>
  );
}

// ── Forecast table ─────────────────────────────────────────────────────────

function ForecastTableRow({
  row,
  isOpen,
  onOpen,
}: {
  row: CustomerForecastSummary;
  isOpen: boolean;
  onOpen: (id: number) => void;
}) {
  const { t } = useTranslation();
  const overdue = isOverdue(row);
  const isCurrent = (() => {
    const now = new Date();
    return row.year === now.getFullYear() && row.month === now.getMonth() + 1;
  })();
  return (
    <tr
      onClick={() => onOpen(row.id)}
      className={`cursor-pointer border-b border-subtle transition-colors hover:surface-base ${
        isOpen ? 'surface-base' : ''
      }`}>
      <td className="px-20 py-16">
        <div className="typo-text-14--bold text-full">
          {fmtYearMonth(row.year, row.month)}
          {isCurrent && <span className="ml-8 typo-text-12 text-faded">({t('forecasts.running')})</span>}
        </div>
      </td>
      <td className="px-20 py-16 text-right typo-text-14 text-full">
        {(() => {
          const original = row.total_pakete;
          const boosted = row.total_pakete_boosted ?? original;
          const hasBoost = boosted > original;
          const carriedOut = row.input_total_packets != null ? row.input_total_packets - original : 0;
          return (
            <div className="flex flex-col items-end">
              <span className="inline-flex items-center justify-end gap-6">
                {boosted ? fmtNumber(boosted) : '—'}
                {carriedOut > 0 && (
                  <Icon
                    name="info"
                    width={16}
                    height={16}
                    hoverText={t('forecasts.carry_out_hint', { count: carriedOut })}
                  />
                )}
              </span>
              {hasBoost && (
                <span className="typo-text-12 text-faded">
                  {t('forecasts.plannedOriginal', { value: fmtNumber(original) })}
                </span>
              )}
            </div>
          );
        })()}
      </td>
      <td className="px-20 py-16 text-center typo-text-14 text-full">
        {row.actuals_filled_days > 0 ? fmtNumber(row.total_actual) : '—'}
      </td>
      <td className="px-20 py-16 text-center typo-text-14 text-full">
        {row.actuals_filled_days > 0 ? fmtNumber(row.total_possible) : '—'}
      </td>
      <td className="px-20 py-16 text-center typo-text-14">
        {(() => {
          const planned = row.total_pakete_boosted ?? row.total_pakete;
          if (row.actuals_filled_days === 0 || !planned) {
            return <span className="text-faded">—</span>;
          }
          const pct = ((row.total_actual - planned) / planned) * 100;
          const cls = Math.abs(pct) < 5 ? 'status-success-full' : 'status-error-full';
          return <span className={cls}>{`${pct > 0 ? '+' : ''}${fmtPercent(pct)}%`}</span>;
        })()}
      </td>
      <td
        className={`px-20 py-16 text-right typo-text-14 ${overdue ? 'status-error-full font-semibold' : 'text-faded'}`}>
        {fmtDate(row.deadline)}
      </td>
      <td className="px-20 py-16">
        {overdue ? (
          <Badge className="max-w-min" label={t('forecasts.state.overdue')} expanded type={BadgeType.Error} />
        ) : (
          <StateBadge state={row.state} />
        )}
      </td>
    </tr>
  );
}

function ForecastTableColgroup() {
  return (
    <colgroup>
      <col style={{ width: '22%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '15%' }} />
      <col style={{ width: '15%' }} />
    </colgroup>
  );
}

function ForecastTableHeader() {
  const { t } = useTranslation();
  return (
    <thead className="surface-base">
      <tr className="border-b border-subtle">
        <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.month_label')}
        </th>
        <th className="px-20 py-12 text-right typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.col.forecast')}
        </th>
        <th className="px-20 py-12 text-center typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.col.actual')}
        </th>
        <th className="px-20 py-12 text-center typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.col.possible')}
        </th>
        <th className="px-20 py-12 text-center typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.col.deviation')}
        </th>
        <th className="px-20 py-12 text-right typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.col.deadline')}
        </th>
        <th className="px-20 py-12 text-left typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.state_label')}
        </th>
      </tr>
    </thead>
  );
}

function ForecastTable({
  rows,
  openId,
  onOpen,
  onAddMonth,
}: {
  rows: CustomerForecastSummary[];
  openId: number | null;
  onOpen: (id: number) => void;
  onAddMonth: () => void;
}) {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);

  const { horizon, history } = useMemo(() => {
    const today = new Date();
    const byDate = (a: CustomerForecastSummary, b: CustomerForecastSummary) =>
      a.year === b.year ? b.month - a.month : b.year - a.year;
    // The planning window opens at the current month + a 1-month gap, so the
    // first plannable month is 2 months ahead (Aug → Oct). "Aktuell zu planen"
    // therefore holds every month from there on (no upper bound), plus any
    // manually-added month. "Verlauf" holds the rest: the running month, the
    // gap month, and everything already past.
    const inHorizon = rows.filter(r => isInPlanningWindow(r, today) || r.manually_added).sort(byDate);
    const outHorizon = rows.filter(r => !isInPlanningWindow(r, today) && !r.manually_added).sort(byDate);
    return { horizon: inHorizon, history: outHorizon };
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="p-40 text-center flex flex-col items-center gap-16">
        <p className="typo-text-14 text-faded">{t('forecasts.empty')}</p>
        <Button tone={ButtonToneProps.PRIMARY_BLUE} icon="plus" title={t('forecasts.addMonth')} onClick={onAddMonth} />
      </div>
    );
  }

  return (
    <div>
      <div className="px-20 pt-16 pb-8 flex items-center justify-between gap-12">
        <span className="typo-text-12 text-faded font-semibold uppercase tracking-wider">
          {t('forecasts.section.toPlan')}
        </span>
        <Button tone={ButtonToneProps.PRIMARY_BLUE} icon="plus" title={t('forecasts.addMonth')} onClick={onAddMonth} />
      </div>
      {horizon.length === 0 ? (
        <div className="px-20 pb-16 typo-text-14 text-faded">{t('forecasts.section.toPlanEmpty')}</div>
      ) : (
        <table className="w-full table-fixed">
          <ForecastTableColgroup />
          <ForecastTableHeader />
          <tbody>
            {horizon.map(row => (
              <ForecastTableRow key={row.id} row={row} isOpen={openId === row.id} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      )}

      {history.length > 0 && (
        <div className="border-t border-subtle">
          <button
            type="button"
            onClick={() => setHistoryOpen(o => !o)}
            className="w-full px-20 py-12 flex items-center justify-between typo-text-12 text-faded font-semibold uppercase tracking-wider hover:text-full">
            <span>{t('forecasts.section.history', { count: history.length })}</span>
            <span aria-hidden>{historyOpen ? '▾' : '▸'}</span>
          </button>
          {historyOpen && (
            <table className="w-full table-fixed">
              <ForecastTableColgroup />
              <ForecastTableHeader />
              <tbody>
                {history.map(row => (
                  <ForecastTableRow key={row.id} row={row} isOpen={openId === row.id} onOpen={onOpen} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline detail (wizard or read-only post-submit view) ───────────────────

function ForecastDetailPanel({
  forecastId,
  onClose,
  onReady,
}: {
  forecastId: number;
  onClose: () => void;
  onReady?: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useForecast(forecastId);
  // Signal the parent once the real content (not the loading placeholder) is in
  // the DOM, so it can scroll to the now full-height panel instead of the short
  // loading box — otherwise the first (uncached) open scrolls too early.
  const readyFired = useRef(false);
  useEffect(() => {
    if (!isLoading && !readyFired.current) {
      readyFired.current = true;
      onReady?.();
    }
  }, [isLoading, onReady]);
  useEffect(() => {
    if (error) toast.error(t('forecasts.loadFailed'));
  }, [error, t]);
  if (isLoading) {
    return (
      <section className="surface-elevated border border-subtle rounded-base card-shadow p-24 typo-text-14 text-faded">
        {t('forecasts.loading')}
      </section>
    );
  }
  if (error || !data) {
    return null;
  }
  // Only `approved` (= System-B has confirmed) is locked: customer must request
  // a change to unlock it. All other states — including `submitted` while
  // pending B-review — are freely editable; edits auto-re-queue the push.
  if (data.state === 'approved') {
    return <ForecastSubmittedView forecast={data} onClose={onClose} />;
  }
  return <ForecastWizard forecast={data} onClose={onClose} />;
}

// ── Add-Month modal ────────────────────────────────────────────────────────

function AddMonthModal({
  existing,
  onClose,
  onAdded,
}: {
  existing: CustomerForecastSummary[];
  onClose: () => void;
  onAdded: (id: number) => void;
}) {
  const { t } = useTranslation();
  const add = useAddForecastMonth();

  // Running month + at most ADD_MONTH_MAX_AHEAD months ahead, minus the months
  // that already have a forecast — see addableMonths().
  const options: Option[] = useMemo(
    () =>
      addableMonths(existing, new Date()).map(m => ({
        label: fmtYearMonth(m.year, m.month),
        value: `${m.year}-${m.month}`,
      })),
    [existing],
  );

  const [value, setValue] = useState(options[0]?.value ?? '');
  const noneLeft = options.length === 0;

  const onConfirm = async () => {
    if (!value) return;
    const [year, month] = value.split('-').map(Number);
    try {
      const forecast = await add.mutateAsync({ year, month });
      onAdded(forecast.id);
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Modal isOpen onClose={onClose} smallSize>
      <div className="max-w-[480px] w-full p-32 flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h4 className="typo-title-24 text-full">{t('forecasts.addMonth')}</h4>
          <p className="typo-text-14 text-faded">{t('forecasts.addMonthHint')}</p>
        </div>
        {noneLeft ? (
          <p className="typo-text-14 text-faded">{t('forecasts.addMonthNoneLeft')}</p>
        ) : (
          <Select
            label={t('forecasts.month')}
            options={options}
            defaultValue={options[0]}
            onChange={o => setValue(o.value)}
            fullWidth
            menuFixed
          />
        )}
        <div className="flex justify-end gap-12">
          <Button tone={ButtonToneProps.SECONDARY} title={t('general.cancel')} onClick={onClose} />
          <Button
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('forecasts.addMonth')}
            onClick={onConfirm}
            disabled={add.isPending || noneLeft}
          />
        </div>
      </div>
    </Modal>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

enum ForecastsTabKey {
  FORECASTS = 'forecasts',
  PROMOTIONS = 'promotions',
}

export default function Forecasts() {
  const { t } = useTranslation();
  const { data: forecasts, isLoading, error } = useOwnForecasts();
  const rows = forecasts || [];
  useEffect(() => {
    if (error) toast.error(t('forecasts.listLoadFailed'));
  }, [error, t]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState<number | null>(() => {
    const requested = Number(searchParams.get('open'));
    return Number.isInteger(requested) && requested > 0 ? requested : null;
  });
  const [addOpen, setAddOpen] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);

  // Called by the detail panel once its real content is rendered (not the
  // loading placeholder). Scroll the full-height panel fully into view — its
  // bottom to the viewport bottom — so it's obvious the editor opened below the
  // (often long) table. Deferred to the next frame so layout is settled.
  const scrollToDetail = () => {
    requestAnimationFrame(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  const activeTab =
    (searchParams.get('tab') as ForecastsTabKey | null) === ForecastsTabKey.PROMOTIONS
      ? ForecastsTabKey.PROMOTIONS
      : ForecastsTabKey.FORECASTS;

  const handleTabChange = (key: ForecastsTabKey) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  const tabs: TabProps[] = [
    {
      label: t('forecasts.tabs.forecasts'),
      key: ForecastsTabKey.FORECASTS,
      onClick: () => handleTabChange(ForecastsTabKey.FORECASTS),
    },
    {
      label: t('forecasts.tabs.promotions'),
      key: ForecastsTabKey.PROMOTIONS,
      onClick: () => handleTabChange(ForecastsTabKey.PROMOTIONS),
    },
  ];
  const activeIndex = tabs.findIndex(tab => tab.key === activeTab);

  return (
    <Container>
      <HeadlineWithButtons headline={t('forecasts.title')} subline={t('forecasts.subtitle')} />

      <TabBar defaultIndex={activeIndex} tabs={tabs} />

      <div className="flex flex-col gap-24">
        {activeTab === ForecastsTabKey.FORECASTS && (
          <>
            {!isLoading && !error && <OverdueBanner rows={rows} onOpen={setOpenId} />}
            {!isLoading && !error && <ForecastAccuracyCard rows={rows} />}

            {!error && (
              <section className="surface-elevated border border-subtle rounded-base card-shadow overflow-hidden">
                {isLoading && <div className="p-32 typo-text-14 text-faded">{t('forecasts.loading')}</div>}
                {!isLoading && (
                  <ForecastTable rows={rows} openId={openId} onOpen={setOpenId} onAddMonth={() => setAddOpen(true)} />
                )}
              </section>
            )}

            {openId !== null && (
              <div key={openId} ref={detailRef} className="rounded-base forecast-detail-highlight scroll-mb-16">
                <ForecastDetailPanel forecastId={openId} onClose={() => setOpenId(null)} onReady={scrollToDetail} />
              </div>
            )}

            {addOpen && (
              <AddMonthModal existing={rows} onClose={() => setAddOpen(false)} onAdded={id => setOpenId(id)} />
            )}
          </>
        )}

        {activeTab === ForecastsTabKey.PROMOTIONS && <PromotionPeriodsSection />}
      </div>

      <Outlet />
    </Container>
  );
}
